import { useState, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { FaStar, FaShoppingCart, FaTruck, FaBox, FaRuler, FaPalette, FaTag, FaUser, FaPhone, FaEnvelope, FaMapMarkerAlt, FaFileAlt, FaPaperPlane, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import productsData from '../data/products.json';
import Emailjs from '../services/Emailjs';
import { DContext } from '../store/DataContext';
// Dynamically resolve product images just like in Products list
const productImages = require.context('../assets/Products', false, /\.(png|jpe?g|webp|svg)$/);
function getImageSrc(filename) {
    if (!filename) return '';
    try {
        return productImages(`./${filename}`);
    } catch (e) {
        return '';
    }
}

function Product() {
    const { productId } = useParams();
    const navigate = useNavigate();
    const { showAlert } = useContext(DContext);

    const product = productsData.find(p => p.productId === productId);

    const [selectedImage, setSelectedImage] = useState(0);
    const [selectedSize, setSelectedSize] = useState('');
    const [selectedColor, setSelectedColor] = useState('');
    const [enquiryOpen, setEnquiryOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        contact: '',
        email: '',
        city: '',
        subject: '',
        description: ''
    });
    const [errors, setErrors] = useState({});

    if (!product) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-800 mb-4">Product Not Found</h1>
                    <p className="text-gray-600 mb-6">The product you're looking for doesn't exist.</p>
                    <Link to="/products" className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition">
                        Browse Products
                    </Link>
                </div>
            </div>
        );
    }

    const discount = product.offerPrice && product.offerPrice < product.actualPrice
        ? Math.round(((product.actualPrice - product.offerPrice) / product.actualPrice) * 100)
        : 0;

    const openEnquiry = () => {
        setFormData(prev => ({
            ...prev,
            subject: `Enquiry for ${product.title} (${product.sku})`,
            description: selectedSize || selectedColor
                ? `Size: ${selectedSize || 'Not selected'}, Color: ${selectedColor || 'Not selected'}`
                : ''
        }));
        setEnquiryOpen(true);
    };

    const closeEnquiry = () => {
        setEnquiryOpen(false);
        setFormData({ name: '', contact: '', email: '', city: '', subject: '', description: '' });
        setErrors({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.name.trim()) newErrors.name = 'Name required';
        else if (formData.name.trim().length < 2) newErrors.name = 'Min 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(formData.name)) newErrors.name = 'Letters & spaces only';

        if (!formData.contact.trim()) newErrors.contact = 'Contact required';
        else if (!/^[6-9]\d{9}$/.test(formData.contact)) newErrors.contact = 'Invalid mobile';

        if (formData.email.trim()) {
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            if (!emailRegex.test(formData.email)) newErrors.email = 'Invalid email';
        }

        if (!formData.city.trim()) newErrors.city = 'City required';
        else if (formData.city.trim().length < 2) newErrors.city = 'Min 2 characters';
        else if (!/^[a-zA-Z\s]+$/.test(formData.city)) newErrors.city = 'Letters & spaces only';

        if (!formData.subject.trim()) newErrors.subject = 'Subject required';
        else if (formData.subject.trim().length < 3) newErrors.subject = 'Min 3 characters';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            showAlert('Fix form errors before submitting');
            return;
        }
        setIsSubmitting(true);
        try {
            await Emailjs({
                from_name: formData.name,
                contact: formData.contact,
                email: formData.email || 'Not provided',
                city: formData.city,
                product: `${product.title} (${product.sku})`,
                subject: formData.subject,
                message: formData.description || 'No additional details'
            });
            await showAlert('Enquiry submitted successfully');
            closeEnquiry();
        } catch (err) {
            console.log("Enquiry submission error:", err);
            showAlert('Failed to submit enquiry');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Back Button */}
            <div className="max-w-7xl mx-auto px-4 pt-8">
                <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-primary-600 transition">
                    <FaArrowLeft /> Back
                </button>
            </div>

            {/* Product Section */}
            <div className="max-w-7xl mx-auto px-4 py-8">
                <div className="grid lg:grid-cols-2 gap-12">
                    {/* Left: Images */}
                    <div>
                        <div className="bg-white rounded-2xl shadow-lg overflow-hidden mb-4 relative">
                            {discount > 0 && (
                                <span className="absolute top-4 left-4 bg-red-600 text-white text-sm font-bold px-3 py-1 rounded-full z-10">-{discount}%</span>
                            )}
                            {product.badge && (
                                <span className="absolute top-4 right-4 bg-primary-600 text-white text-sm font-bold px-3 py-1 rounded-full z-10">{product.badge}</span>
                            )}
                            <img
                                src={getImageSrc(product.images[selectedImage])}
                                alt={product.title}
                                className="w-full h-96 object-cover"
                            />
                        </div>
                        {product.images.length > 1 && (
                            <div className="flex gap-3">
                                {product.images.map((img, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => setSelectedImage(idx)}
                                        className={`flex-1 rounded-lg overflow-hidden border-2 transition ${selectedImage === idx ? 'border-primary-600' : 'border-gray-200 hover:border-gray-400'
                                            }`}
                                    >
                                        <img src={getImageSrc(img)} alt={`${product.title} ${idx + 1}`} className="w-full h-24 object-cover" />
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Right: Details */}
                    <div>
                        <div className="inline-block px-3 py-1 bg-primary-50 text-primary-600 text-xs font-semibold rounded-full mb-3">
                            {product.category.toUpperCase()}
                        </div>
                        <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-3">{product.title}</h1>
                        <div className="flex items-center gap-3 mb-4">
                            <div className="flex items-center gap-1">
                                {[...Array(5)].map((_, i) => (
                                    <FaStar key={i} className={i < product.rating ? 'text-yellow-400' : 'text-gray-300'} />
                                ))}
                                <span className="text-sm text-gray-600 ml-2">({product.rating}/5)</span>
                            </div>
                            <span className="text-sm text-gray-500">SKU: {product.sku}</span>
                        </div>

                        {/* Price */}
                        <div className="mb-6 flex items-baseline gap-3">
                            {product.offerPrice && product.offerPrice < product.actualPrice ? (
                                <>
                                    <span className="text-4xl font-bold text-primary-600">₹{product.offerPrice}</span>
                                    <span className="text-2xl line-through text-gray-400">₹{product.actualPrice}</span>
                                    <span className="text-sm font-semibold text-red-600 bg-red-50 px-2 py-1 rounded">Save ₹{product.actualPrice - product.offerPrice}</span>
                                </>
                            ) : (
                                <span className="text-4xl font-bold text-gray-800">₹{product.actualPrice}</span>
                            )}
                        </div>

                        {/* Description */}
                        <p className="text-gray-700 mb-6 leading-relaxed">{product.desc || 'High-quality garment from SS Tex with premium fabric and craftsmanship.'}</p>

                        {/* Sizes */}
                        {product.sizes && product.sizes.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <FaRuler className="text-primary-600" />
                                    <h3 className="font-semibold text-gray-800">Select Size</h3>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {product.sizes.map((s, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedSize(s.label)}
                                            className={`px-5 py-2 rounded-lg font-semibold border-2 transition ${selectedSize === s.label
                                                ? 'bg-primary-600 text-white border-primary-600'
                                                : s.stock > 0
                                                    ? 'bg-white text-gray-700 border-gray-300 hover:border-primary-600'
                                                    : 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                                                }`}
                                            disabled={s.stock === 0}
                                        >
                                            {s.label}
                                            {s.stock === 0 && <span className="block text-xs">Out</span>}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Colors */}
                        {product.colors && product.colors.length > 0 && (
                            <div className="mb-6">
                                <div className="flex items-center gap-2 mb-3">
                                    <FaPalette className="text-primary-600" />
                                    <h3 className="font-semibold text-gray-800">Available Colors</h3>
                                </div>
                                <div className="flex flex-wrap gap-3">
                                    {product.colors.map((c, idx) => (
                                        <button
                                            key={idx}
                                            onClick={() => setSelectedColor(c)}
                                            className={`px-4 py-2 rounded-lg font-medium border-2 transition capitalize ${selectedColor === c
                                                ? 'bg-primary-600 text-white border-primary-600'
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-600'
                                                }`}
                                        >
                                            {c}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Info Grid */}
                        <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 rounded-xl p-4">
                            <div className="flex items-center gap-2">
                                <FaBox className="text-primary-600" />
                                <div>
                                    <div className="text-xs text-gray-500">MOQ</div>
                                    <div className="font-semibold text-gray-800">{product.moq} pcs</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaCheckCircle className={product.stockStatus === 'in-stock' ? 'text-green-600' : 'text-orange-600'} />
                                <div>
                                    <div className="text-xs text-gray-500">Stock</div>
                                    <div className="font-semibold text-gray-800 capitalize">{product.stockStatus.replace('-', ' ')}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaTruck className="text-primary-600" />
                                <div>
                                    <div className="text-xs text-gray-500">Fabric</div>
                                    <div className="font-semibold text-gray-800 capitalize">{product.fabric}</div>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <FaTag className="text-primary-600" />
                                <div>
                                    <div className="text-xs text-gray-500">Status</div>
                                    <div className="font-semibold text-gray-800 capitalize">{product.productStatus}</div>
                                </div>
                            </div>
                        </div>

                        {/* CTA */}
                        <button
                            onClick={openEnquiry}
                            className="w-full bg-primary-600 hover:bg-primary-700 text-white font-bold py-4 rounded-xl transition transform hover:scale-105 flex items-center justify-center gap-3 shadow-lg text-lg"
                        >
                            <FaShoppingCart /> Send Enquiry
                        </button>
                    </div>
                </div>

                {/* Additional Info */}
                <div className="mt-12 grid md:grid-cols-2 gap-8">
                    {/* Tags */}
                    {product.tags && product.tags.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-md p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <FaTag className="text-primary-600" /> Product Tags
                            </h3>
                            <div className="flex flex-wrap gap-2">
                                {product.tags.map((t, idx) => (
                                    <span key={idx} className="px-3 py-1 bg-primary-50 text-primary-600 rounded-full text-sm border border-primary-100">
                                        {t}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Care Instructions */}
                    {product.careInstructions && product.careInstructions.length > 0 && (
                        <div className="bg-white rounded-2xl shadow-md p-6">
                            <h3 className="text-xl font-bold text-gray-800 mb-4">Care Instructions</h3>
                            <ul className="space-y-2">
                                {product.careInstructions.map((instruction, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                                        <FaCheckCircle className="text-green-600 mt-1 flex-shrink-0" />
                                        <span>{instruction}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>

            {/* Enquiry Modal */}
            {enquiryOpen && (
                <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4">
                    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={closeEnquiry} />
                    <div className="relative bg-white w-full max-w-xl mx-auto rounded-2xl shadow-2xl p-8 mt-10">
                        <h2 className="text-2xl font-bold mb-1">Product Enquiry</h2>
                        <p className="text-xs text-gray-500 mb-4">{product.title} ({product.sku})</p>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                                <div className="relative">
                                    <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="name" value={formData.name} onChange={handleChange} type="text" placeholder="Your full name" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.name ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Contact *</label>
                                <div className="relative">
                                    <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="contact" value={formData.contact} onChange={handleChange} type="tel" maxLength={10} placeholder="10-digit mobile" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.contact ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.contact && <p className="text-xs text-red-500 mt-1">{errors.contact}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Email (Optional)</label>
                                <div className="relative">
                                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="email" value={formData.email} onChange={handleChange} type="email" placeholder="you@example.com" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">City *</label>
                                <div className="relative">
                                    <FaMapMarkerAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="city" value={formData.city} onChange={handleChange} type="text" placeholder="Your city" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.city ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.city && <p className="text-xs text-red-500 mt-1">{errors.city}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Subject *</label>
                                <div className="relative">
                                    <FaFileAlt className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                    <input name="subject" value={formData.subject} onChange={handleChange} type="text" placeholder="Short subject" className={`w-full pl-10 pr-3 py-2 rounded-lg border-2 focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.subject ? 'border-red-500' : 'border-gray-300'}`} />
                                </div>
                                {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
                            </div>
                            <div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1">Description (Optional)</label>
                                <textarea name="description" value={formData.description} onChange={handleChange} rows={3} placeholder="Additional details..." className="w-full px-3 py-2 rounded-lg border-2 border-gray-300 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button type="button" onClick={closeEnquiry} className="flex-1 py-3 rounded-lg border border-gray-300 font-semibold text-gray-700 hover:bg-gray-100 transition">Cancel</button>
                                <button type="submit" disabled={isSubmitting} className={`flex-1 py-3 rounded-lg font-semibold flex items-center justify-center gap-2 text-white bg-primary-600 hover:bg-primary-700 transition ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                                    {isSubmitting ? (<><div className="animate-spin h-5 w-5 border-b-2 border-white rounded-full" /><span>Sending...</span></>) : (<><FaPaperPlane /><span>Send</span></>)}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default Product;